import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Divider,
  Grid,
  Header,
  Segment,
  Table,
  Icon,
  Accordion,
  Button,
  Modal,
  Checkbox
} from 'semantic-ui-react';
import { useLocation } from 'react-router-dom';
import { history } from '../../_helpers/';
import { servicesAPI } from '../../_services';
import { useCurrentUser } from '../../_services';
import { setToast } from '../../store/ToastManagement';
import { copyToClipboard, commandToText } from '../../utils';
import { ChargePoint } from '../../_components/map/Features/ChargePoint';
import { dbUTCDatetimeToLocal } from '../../utils';
import { Map } from './Map';
import Moment from 'moment-timezone';
import { extendMoment } from 'moment-range';

const moment = extendMoment(Moment);

export const DisplayHUB = () => {
  const location = useLocation();
  const hydraRef = location.pathname.split('/')[3];

  const { currentUser, currentUserCan } = useCurrentUser();

  const [hubLocationInfo, setHubLocationInfo] = useState(
    window.hubLocationInfo
  );
  const [loadingHubInfo, setLoadingHubInfo] = useState(true);
  const [refresh, setRefresh] = useState(1);
  const [hydra, setHydra] = useState(null);
  const [phalanxes, setPhalanxes] = useState(null);
  const [hubInterfacePhalanx, setHubInterfacePhalanx] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [phalanxData, setPhalanxData] = useState([]);
  const [lastUpdated, setLastUpdated] = useState('');
  const [customerNames, setCustomerNames] = useState([]);
  const [customerIds, setCustomerIds] = useState([]);

  const [mapCenter, setMapCenter] = useState([0, 0]);
  const [mapZoom, setMapZoom] = useState(18);

  const [mapHide, setMapHide] = useState(false);
  const [statusHide, setStatusHide] = useState(false);

  const [command, setCommand] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [where, setWhere] = useState('');
  const [isAXON, setIsAXON] = useState(null);
  const [isAON, setIsAON] = useState(null);
  const [cleaningActive, setCleaningActive] = useState(false);
  const [cleaningSelected, setCleaningSelected] = useState([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]); // eslint-disable-line

  const dispatch = useDispatch();

  const NO_UID = '00000000-0000-0000-0000-000000000000';

  useEffect(() => {
    const timerInterval = setInterval(() => {
      setRefresh(Math.random());
    }, 10000);

    // This useEffect cleanup function prevents the Javascript timer running forever
    return () => {
      clearInterval(timerInterval);
    };
  }, []);

  useEffect(() => {
    if (!hubLocationInfo && loadingHubInfo) {
      servicesAPI
        .InventoryServices('getPhalanxLocationInfo')
        .then(hubLocationInfo => {
          let newHubLocationInfo = [];
          for (const hydraRef of Object.keys(hubLocationInfo)) {
            newHubLocationInfo.push({
              ...hubLocationInfo[hydraRef],
              reference: hydraRef
            });
          }
          window.hubLocationInfo = newHubLocationInfo;

          setLoadingHubInfo(false);
          setHubLocationInfo(newHubLocationInfo);
        });

      // Retrieve cleaning selection
      let cleaningData = localStorage.getItem('hubCleaning');
      if (!cleaningData) return;
      cleaningData = JSON.parse(cleaningData);
      if (cleaningData.reference !== hydraRef) return;

      setCleaningSelected(cleaningData.selected);
      setCleaningActive(true);
    }
  }, [hubLocationInfo, loadingHubInfo]);

  async function getData() {
    try {
      let type = isAON ? 'HOME' : isAXON ? 'AXON' : 'Hydra';

      // Check for outdated timestamps
      const dtLastHeartbeat = moment.utc(
        hydra.last_heartbeat_time,
        'YYYY-MM-DDTHH:mm:ss,Z'
      );
      const dtNow = moment.utc().local();
      const elapsedMilliseconds = moment.duration(dtNow.diff(dtLastHeartbeat))
        ._milliseconds;

      // Flag comms lost
      hydra.no_heartbeat = elapsedMilliseconds >= 900 * 1000;

      setTableData([
        {
          property: 'Reference',
          value: hydra.reference
        },
        {
          property: 'Type',
          value: type
        },
        {
          property: 'Local Authority',
          value: hydra.local_authority
        },
        {
          property: 'Postcode',
          value: hydra.postcode
        },
        {
          property: 'Status',
          value: hydra.status
        },
        {
          property: 'Tag Status',
          value: ['COMMISSIONING', 'MAINTENANCE'].includes(hydra.tag_status) ? (
            <p style={{ color: 'red' }}>{hydra.tag_status}</p>
          ) : (
            hydra.tag_status
          )
        },
        {
          property: 'Current Limit (A)',
          value: hydra.working_current_limit
        },
        {
          property: 'Last Heartbeat',
          value: hydra.no_heartbeat ? (
            <p style={{ color: 'red' }}>
              {dbUTCDatetimeToLocal(hydra.last_heartbeat_time)}
            </p>
          ) : (
            dbUTCDatetimeToLocal(hydra.last_heartbeat_time)
          )
        }
      ]);
    } catch (error) {}
  }

  useEffect(() => {
    servicesAPI
      .InventoryServices('getHydraByReference', hydraRef, false)
      .then(hydra => {
        servicesAPI
          .InventoryServices('getHydraPhalanxes', hydra.tag_id)
          .then(phalanxes => {
            const hubInterfacePhalanx = phalanxes.filter(p =>
              p.reference.endsWith('cs_00')
            );
            if (hubInterfacePhalanx.length)
              setHubInterfacePhalanx(hubInterfacePhalanx[0]);

            setPhalanxes(
              phalanxes.filter(
                p =>
                  p.tag_status !== 'INACTIVE' &&
                  p?.reference &&
                  !p.reference.endsWith('cs_00')
              )
            );
          });

        setHydra(hydra);
        setIsAXON(hydra.project === 'AXON' || hydra.project === 'AON');
        setIsAON(hydra.project === 'AON');
      });
  }, [refresh]);

  useEffect(() => {
    if (isAXON === null || isAON === null || !hydra) return;

    getData();
    setWhere(hydra.description);
  }, [hydra, isAXON, isAON]);

  useEffect(() => {
    if (hubLocationInfo && phalanxes && phalanxes.length) {
      servicesAPI
        .AdminServices('getHydraPhalanxLastStatusEntries', hydraRef)
        .then(statusEntries => {
          setPhalanxData(
            phalanxes.map((p, idx) => {
              const connected_lance =
                p.connected_lance_tag_id !== NO_UID && p.connected_lance_tag_id;

              const commissioning = p.tag_status === 'COMMISSIONING';
              const maintenance = p.tag_status === 'MAINTENANCE';
              const capped = p.status === 'Capped';
              const muted = p.status === 'Stopped';
              const faulted = p.status === 'Faulted';
              const charging =
                p.status === 'Occupied' &&
                p.connected_lance_tag_id !== NO_UID &&
                p.connected_lance_tag_id &&
                p.transaction_id !== NO_UID;

              // Check for outdated timestamps
              const dtLastHeartbeat = moment.utc(
                p.last_heartbeat_time,
                'YYYY-MM-DDTHH:mm:ss,Z'
              );
              const dtNow = moment.utc().local();
              const elapsedMilliseconds = moment.duration(
                dtNow.diff(dtLastHeartbeat)
              )._milliseconds;

              // Flag comms lost
              p.no_heartbeat = elapsedMilliseconds >= 900 * 1000;

              const statusEntry = statusEntries.find(
                se => se.phalanx_ref === p.reference
              );

              return {
                ...p,
                idx: idx,
                connected_lance: connected_lance,
                charging: charging,
                apc_limit: p.apc_limit,
                boost: p.boost,
                commissioning: commissioning,
                maintenance: maintenance,
                capped: capped,
                muted: muted,
                faulted: faulted,
                axon_fw_version: p.axon_fw_version,
                cart_fw_version: p.cart_fw_version,
                connected_lance_fw_version: p.connected_lance_fw_version,
                last_heartbeat_time: p.last_heartbeat_time,
                no_heartbeat: p.no_heartbeat,
                open: statusEntry?.open,
                closed: statusEntry?.closed
              };
            })
          );
          setLastUpdated(moment.utc().format('HH:mm:ss'));

          getCustomerNames(phalanxes);

          for (let i = 0; i < hubLocationInfo.length; i++) {
            const hydraLocation = hubLocationInfo[i];

            if (hydraLocation.reference === hydraRef) {
              const latLon =
                hydraLocation.lat_lons[
                  Math.floor(hydraLocation.lat_lons.length / 2)
                ];

              setMapCenter([latLon[1], latLon[0]]);
            }
          }
        });
    }
  }, [hubLocationInfo, phalanxes, refresh]);

  const getCustomerNames = phalanxes => {
    const lanceTagIds = phalanxes
      .map(p => p.connected_lance_tag_id)
      .filter(lanceTagId => {
        return lanceTagId && lanceTagId !== NO_UID;
      });

    if (lanceTagIds.length) {
      const values = {
        tag_ids: lanceTagIds
      };

      servicesAPI
        .CustomerServices('getCustomerNamesFromLanceTagIds', values)
        .then(
          response => {
            if (!response) return;

            const names = phalanxes.map(p => {
              let customer = null;
              if (p.connected_lance_tag_id !== NO_UID) {
                customer = response.find(
                  c => c.lance_tag_id === p.connected_lance_tag_id
                );
              }

              return customer ? customer.forename + ' ' + customer.surname : '';
            });

            setCustomerNames(names);

            const ids = phalanxes.map(p => {
              let customer = null;
              if (p.connected_lance_tag_id !== NO_UID) {
                customer = response.find(
                  c => c.lance_tag_id === p.connected_lance_tag_id
                );
              }
              return customer ? customer.customer_id : '';
            });

            setCustomerIds(ids);
          },
          error => {}
        );
    }
  };

  const confirmSendCommand = (command, confirmation) => {
    setIsModalOpen(true);
    setCommand(command);
    setConfirmation(confirmation);
  };

  const sendCommand = () => {
    setIsModalOpen(false);

    if (command === 'COPY_DB_TO_REDIS') {
      servicesAPI
        .InventoryServices('getHydra', hydra.tag_id, true)
        .then(response => {
          dispatch(
            setToast({
              title: 'Cache refreshed!',
              body: ''
            })
          );
        });
      return;
    } else if (command === 'RESTART_HYDRA') {
      const timestamp = moment.utc().format('YYYY-MM-DDTHH:mm:ss') + ',Z';

      servicesAPI.InventoryServices('getHydraPhalanxes', hydra.tag_id).then(
        phalanxes => {
          let availablePhalanxes = [];
          phalanxes.forEach(p => {
            if (!p.reference.endsWith('cs_00') && p.status === 'Available')
              availablePhalanxes.push(p);
          });

          if (availablePhalanxes.length) {
            const availablePhalanx =
              availablePhalanxes[
                Math.floor(Math.random() * availablePhalanxes.length)
              ];

            const values = {
              csms_command: command + ',' + timestamp
            };
            servicesAPI
              .InventoryServices(
                'updatePhalanxByReference',
                values,
                availablePhalanx.reference
              )
              .then(
                response => {
                  const csId = availablePhalanx.reference.slice(-2);
                  dispatch(
                    setToast({
                      title: `Command sent to HUB via CS${csId}!`,
                      body: 'Check Google Cloud logs to confirm'
                    })
                  );
                  servicesAPI.AdminServices(
                    'auditUserActivity',
                    {
                      entity_type: 'HYDRA',
                      entity_id: hydra.reference,
                      activity: 'SupportOperation',
                      notes: `${where}: ${commandToText(command)}`,
                      email_subject: '',
                      email_body: ''
                    },
                    currentUser.id
                  );
                },
                error => {}
              );
          }
        },
        error => {}
      );
    } else if (
      [
        'CLEANING_SELECT',
        'CLEANING_STOP',
        'CLEANING_UNLOCK',
        'CLEANING_LOCK',
        'CLEANING_START'
      ].includes(command)
    ) {
      if (command === 'CLEANING_SELECT') {
        if (!phalanxes) {
          setCleaningActive(false);
          return;
        }

        const cleaningSelected = phalanxData.map(p => {
          // check heartbeat is up to date
          return (
            !p.reference.endsWith('cs_00') &&
            p.tag_status === 'ACTIVE' &&
            p.status === 'Available' &&
            (p.connected_lance_tag_id === NO_UID ||
              !p.connected_lance_tag_id) &&
            p.current_state === 'IdlingState'
          );
        });

        setCleaningSelected(cleaningSelected);
        setCleaningActive(true);

        // Store cleaning selection
        const data = {
          reference: hydraRef,
          selected: cleaningSelected
        };
        localStorage.setItem('hubCleaning', JSON.stringify(data));

        return;
      } else {
        if (!cleaningActive) return;

        let auditOperation = '';
        let csmsCommand = '';

        if (command === 'CLEANING_STOP') {
          auditOperation = 'CleaningStoppingCSs';
          csmsCommand = 'STOP_CHARGE_STATION';
        } else if (command === 'CLEANING_UNLOCK') {
          auditOperation = 'CleaningUnlockingCSs';
          csmsCommand = 'UNLOCK_CARTRIDGE';
        } else if (command === 'CLEANING_LOCK') {
          auditOperation = 'CleaningLockingCSs';
          csmsCommand = 'LOCK_CARTRIDGE';
        } else if (command === 'CLEANING_START') {
          auditOperation = 'CleaningStartingCSs';
          csmsCommand = 'START_CHARGE_STATION';
        }

        if (csmsCommand) {
          const timestamp = moment.utc().format('YYYY-MM-DDTHH:mm:ss') + ',Z';
          const values = {
            csms_command: csmsCommand + ',' + timestamp
          };

          phalanxData.forEach((p, idx) => {
            if (cleaningSelected[idx]) {
              servicesAPI.InventoryServices(
                'updatePhalanxByReference',
                values,
                p.reference
              );
            }
          });

          setRefresh(Math.random());

          dispatch(
            setToast({
              title: 'Command sent to selected CSs!',
              body: ''
            })
          );
          servicesAPI.AdminServices(
            'auditUserActivity',
            {
              entity_type: 'HYDRA',
              entity_id: hydra.reference,
              activity: 'SupportOperation',
              notes: `${where}: ${auditOperation}`,
              email_subject: '',
              email_body: ''
            },
            currentUser.id
          );
        }
      }
    } else {
      let values = {};

      if (command === 'STATUS_ACTIVE' || command === 'STATUS_MAINTENANCE') {
        values['tag_status'] =
          command === 'STATUS_ACTIVE' ? 'ACTIVE' : 'MAINTENANCE';
      } else {
        return;
      }

      servicesAPI
        .InventoryServices('updateHydra', values, hydra.tag_id)
        .then(response => {
          servicesAPI
            .InventoryServices(
              'updatePhalanxesByHydraReference',
              values,
              hydra.reference
            )
            .then(response => {
              setRefresh(Math.random());

              dispatch(
                setToast({
                  title: 'Hydra/HUB and charge stations updated!',
                  body: ''
                })
              );
              servicesAPI.AdminServices(
                'auditUserActivity',
                {
                  entity_type: 'HYDRA',
                  entity_id: hydra.reference,
                  activity: 'SupportOperation',
                  notes: `${where}: ${commandToText(command)}`,
                  email_subject: '',
                  email_body: ''
                },
                currentUser.id
              );
            });
        });
    }
  };

  const displayState = p => {
    if (p.current_state === 'DisabledState') {
      return <p style={{ color: 'red' }}>{p.current_state}</p>;
    }

    if (p.axon_stopped) {
      return (
        <p style={{ color: 'red' }}>
          {p.current_state}
          <br />
          (AXON stopped CS to prevent lance theft)
        </p>
      );
    }

    return p.current_state;
  };

  if (!hydra) return <div>Loading...</div>;

  return (
    <Segment>
      <Grid stretched>
        <Grid.Row>
          <Grid.Column>
            <Header as="h2">
              HUB:
              {hydra ? ` ${hydra.description}` : ''}
            </Header>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Divider hidden />
      <Divider hidden />
      <Grid>
        <Accordion>
          <Accordion.Title
            active={!statusHide}
            index={0}
            onClick={() => {
              setStatusHide(!statusHide);
            }}
          >
            <Header as="h2">
              <Icon name="dropdown" />
              Status
            </Header>
          </Accordion.Title>
          <Accordion.Content active={!statusHide}>
            <Table
              basic="very"
              selectable
              stackable
              renderBodyRow={({ property, value }, idx) => ({
                key: idx,
                cells: [
                  {
                    key: 'property',
                    content: property
                  },
                  {
                    key: 'value',
                    content: value
                  }
                ]
              })}
              tableData={tableData}
            />
            {currentUserCan('ManageAxonHubInterface') && hubInterfacePhalanx && (
              <>
                <Button
                  onClick={() => {
                    const bits = hubInterfacePhalanx.reference.split('_cs');
                    const hubRef = bits[0];

                    history.push(`/admin/hubs/${hubRef}/hub_if`);
                  }}
                  color="olive"
                >
                  View HUB Interface
                </Button>
                <br />
                <br />
              </>
            )}
            <Table basic="very" selectable stackable celled fixed>
              <Table.Header>
                <Table.Row>
                  {cleaningActive ? (
                    <Table.HeaderCell style={{ width: '30px' }} />
                  ) : (
                    ''
                  )}
                  <Table.HeaderCell style={{ width: '60px' }} />
                  {cleaningActive ? (
                    <>
                      <Table.HeaderCell style={{ width: '60px' }}>
                        Open
                      </Table.HeaderCell>
                      <Table.HeaderCell style={{ width: '65px' }}>
                        Closed
                      </Table.HeaderCell>
                    </>
                  ) : (
                    ''
                  )}
                  <Table.HeaderCell style={{ width: '80px' }}>
                    Tag Status
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '70px' }}>
                    Status
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '120px' }}>
                    State
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '140px' }}>
                    Customer
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '70px' }}>
                    Cartridge Type
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '70px' }}>
                    Phase
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '80px' }}>
                    Arbitrated Limit
                  </Table.HeaderCell>
                  {hydra?.project === 'SMARTSTEP' ? (
                    <>
                      <Table.HeaderCell style={{ width: '70px' }}>
                        APC Limit (%)
                      </Table.HeaderCell>
                      <Table.HeaderCell style={{ width: '70px' }}>
                        APC Boost
                      </Table.HeaderCell>
                    </>
                  ) : (
                    ''
                  )}
                  <Table.HeaderCell style={{ width: '130px' }}>
                    Last Heartbeat
                  </Table.HeaderCell>
                  {!cleaningActive ? (
                    <>
                      {isAXON ? (
                        <Table.HeaderCell style={{ width: '70px' }}>
                          AXON FW Version
                        </Table.HeaderCell>
                      ) : (
                        ''
                      )}
                      <Table.HeaderCell style={{ width: '70px' }}>
                        Cartridge FW Version
                      </Table.HeaderCell>
                      <Table.HeaderCell style={{ width: '70px' }}>
                        Lance FW Version
                      </Table.HeaderCell>
                    </>
                  ) : (
                    ''
                  )}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {phalanxData.length &&
                  phalanxData.map((p, p_idx) => (
                    <Table.Row key={p_idx}>
                      {cleaningActive ? (
                        <Table.Cell style={{ textAlign: 'center' }}>
                          <Checkbox
                            checked={cleaningSelected[p_idx]}
                            disabled={true}
                          />
                        </Table.Cell>
                      ) : (
                        ''
                      )}
                      <Table.Cell
                        style={{ textAlign: 'center' }}
                        onClick={() => {
                          copyToClipboard(p.tag_id);

                          const bits = p.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        <ChargePoint
                          w={48}
                          h={48}
                          colour={
                            p.commissioning
                              ? 'orange'
                              : p.maintenance
                              ? 'orange'
                              : p.faulted
                              ? 'red'
                              : p.muted || p.capped
                              ? 'grey'
                              : p.charging
                              ? 'blue'
                              : 'green'
                          }
                          cpId={String(p_idx + 1).padStart(2, '0')}
                          l1={p.charging}
                          l2={p.charging}
                          l3={p.charging}
                          car={p.charging}
                          lance={p.charging}
                        />
                      </Table.Cell>
                      {cleaningActive ? (
                        <>
                          <Table.Cell style={{ textAlign: 'center' }}>
                            {p.open ? 'Y' : 'N'}
                          </Table.Cell>
                          <Table.Cell style={{ textAlign: 'center' }}>
                            {p.closed ? 'Y' : 'N'}
                          </Table.Cell>
                        </>
                      ) : (
                        ''
                      )}
                      <Table.Cell
                        onClick={() => {
                          copyToClipboard(p.status);

                          const bits = p.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        {p.commissioning ? (
                          <p style={{ color: 'red' }}>COMMISSIONING</p>
                        ) : p.maintenance ? (
                          <p style={{ color: 'red' }}>MAINTENANCE</p>
                        ) : (
                          p.tag_status
                        )}
                      </Table.Cell>
                      <Table.Cell
                        onClick={() => {
                          copyToClipboard(p.status);

                          const bits = p.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        {p.capped ? (
                          'Capped'
                        ) : p.muted ? (
                          'Muted'
                        ) : p.faulted ? (
                          <p style={{ color: 'red' }}>{'Faulted'}</p>
                        ) : p.charging ? (
                          <p style={{ color: 'blue' }}>{'Charging'}</p>
                        ) : (
                          'Available'
                        )}
                      </Table.Cell>
                      <Table.Cell
                        onClick={() => {
                          copyToClipboard(p.status);

                          const bits = p.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        {displayState(p)}
                      </Table.Cell>
                      <Table.Cell
                        onClick={() => {
                          if (customerIds.length)
                            history.push(
                              `/admin/customer/${customerIds[p_idx]}`
                            );
                        }}
                      >
                        {customerNames.length ? customerNames[p_idx] : '?'}
                      </Table.Cell>
                      <Table.Cell
                        onClick={() => {
                          const bits = p.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        {p.cart_model === 'NEW' ? (
                          <p style={{ color: 'blue' }}>NEW</p>
                        ) : (
                          'Standard'
                        )}
                      </Table.Cell>
                      <Table.Cell
                        onClick={() => {
                          const bits = p.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        {p.phase}
                      </Table.Cell>
                      <Table.Cell
                        onClick={() => {
                          const bits = p.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        {p.arbitrated_limit}
                      </Table.Cell>
                      {hydra?.project === 'SMARTSTEP' ? (
                        <>
                          <Table.Cell
                            onClick={() => {
                              const bits = p.reference.split('_cs');
                              const hubRef = bits[0];
                              const csId = 'cs' + bits[1];

                              history.push(`/admin/hubs/${hubRef}/${csId}`);
                            }}
                          >
                            {p.apc_limit}
                          </Table.Cell>
                          <Table.Cell
                            onClick={() => {
                              const bits = p.reference.split('_cs');
                              const hubRef = bits[0];
                              const csId = 'cs' + bits[1];

                              history.push(`/admin/hubs/${hubRef}/${csId}`);
                            }}
                          >
                            {p.boost === 1 ? (
                              <p style={{ color: 'blue' }}>{'ACTIVE'}</p>
                            ) : (
                              '-'
                            )}
                          </Table.Cell>
                        </>
                      ) : (
                        ''
                      )}
                      <Table.Cell
                        onClick={() => {
                          const bits = p.reference.split('_cs');
                          const hubRef = bits[0];
                          const csId = 'cs' + bits[1];

                          history.push(`/admin/hubs/${hubRef}/${csId}`);
                        }}
                      >
                        {p.no_heartbeat ? (
                          <p style={{ color: 'red' }}>
                            {dbUTCDatetimeToLocal(p.last_heartbeat_time)}
                          </p>
                        ) : (
                          dbUTCDatetimeToLocal(p.last_heartbeat_time)
                        )}
                      </Table.Cell>
                      {!cleaningActive ? (
                        <>
                          {isAXON ? (
                            <Table.Cell
                              onClick={() => {
                                const bits = p.reference.split('_cs');
                                const hubRef = bits[0];
                                const csId = 'cs' + bits[1];

                                history.push(`/admin/hubs/${hubRef}/${csId}`);
                              }}
                            >
                              {p.axon_fw_version}
                            </Table.Cell>
                          ) : (
                            ''
                          )}
                          <Table.Cell
                            onClick={() => {
                              const bits = p.reference.split('_cs');
                              const hubRef = bits[0];
                              const csId = 'cs' + bits[1];

                              history.push(`/admin/hubs/${hubRef}/${csId}`);
                            }}
                          >
                            {p.cart_fw_version}
                          </Table.Cell>
                          <Table.Cell
                            onClick={() => {
                              const bits = p.reference.split('_cs');
                              const hubRef = bits[0];
                              const csId = 'cs' + bits[1];

                              history.push(`/admin/hubs/${hubRef}/${csId}`);
                            }}
                          >
                            {p.connected_lance_fw_version}
                          </Table.Cell>
                        </>
                      ) : (
                        ''
                      )}
                    </Table.Row>
                  ))}
              </Table.Body>
            </Table>
            <p>Last updated: {lastUpdated}</p>
            <br />
            {(currentUserCan('DoAllTheThings') ||
              currentUserCan('MaintainChargePoint') ||
              currentUserCan('MaintenanceMode')) && (
              <Header as="h2">Portal</Header>
            )}
            {currentUserCan('DoAllTheThings') && (
              <>
                <Button
                  onClick={() => {
                    confirmSendCommand(
                      'COPY_DB_TO_REDIS',
                      'Are you sure you want to refresh the cache to match inventory.hydra?'
                    );
                  }}
                  color="olive"
                >
                  Refresh DB cache
                </Button>
                <br />
                <br />
              </>
            )}
            {currentUserCan('MaintainChargePoint') &&
            hydra?.tag_status === 'COMMISSIONING' ? (
              <>
                <Button
                  onClick={() => {
                    confirmSendCommand(
                      'STATUS_ACTIVE',
                      where +
                        ': Are you sure you want to make the Hydra/HUB and all its charge stations active?'
                    );
                  }}
                  color="olive"
                >
                  Exit commissioning mode
                </Button>
                <br />
                <br />
              </>
            ) : (
              ''
            )}
            {currentUserCan('MaintenanceMode') &&
            hydra?.tag_status !== 'COMMISSIONING' ? (
              <>
                {hydra?.tag_status === 'MAINTENANCE' ? (
                  <Button
                    onClick={() => {
                      confirmSendCommand(
                        'STATUS_ACTIVE',
                        where +
                          ': Are you sure you want to make the Hydra/HUB and all its charge stations active?'
                      );
                    }}
                    color="olive"
                  >
                    Exit maintenance mode
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      confirmSendCommand(
                        'STATUS_MAINTENANCE',
                        where +
                          ': Are you sure you want to put the Hydra/HUB and all its charge station into maintenance mode?'
                      );
                    }}
                    color="olive"
                  >
                    Enter maintenance mode
                  </Button>
                )}
                <br />
                <br />
              </>
            ) : (
              ''
            )}
            {currentUserCan('BetaTest') && isAXON ? (
              <>
                <Header as="h2">Cleaning</Header>
                <Button
                  onClick={() => {
                    confirmSendCommand(
                      'CLEANING_SELECT',
                      where +
                        ': Are you sure you want to select the CSs to clean?'
                    );
                  }}
                  color="olive"
                  disabled={!isAXON || cleaningActive}
                >
                  Start cleaning
                </Button>
                <br />
                <br />
                {isAXON ? (
                  <>
                    <Button
                      onClick={() => {
                        confirmSendCommand(
                          'CLEANING_STOP',
                          where +
                            ': Are you sure you want to stop the selected charge stations?'
                        );
                      }}
                      color="olive"
                      disabled={!isAXON || !cleaningActive}
                    >
                      Stop selected CSs
                    </Button>
                    <br />
                    <br />
                  </>
                ) : (
                  ''
                )}
                <Button
                  onClick={() => {
                    confirmSendCommand(
                      'CLEANING_UNLOCK',
                      where +
                        ': Are you sure you want to unlock the selected charge stations?'
                    );
                  }}
                  color="olive"
                  disabled={!isAXON || !cleaningActive}
                >
                  Unlock selected CSs
                </Button>
                <br />
                <br />
                <Button
                  onClick={() => {
                    confirmSendCommand(
                      'CLEANING_LOCK',
                      where +
                        ': Are you sure you want to lock the selected charge stations?'
                    );
                  }}
                  color="olive"
                  disabled={!isAXON || !cleaningActive}
                >
                  Lock selected CSs
                </Button>
                <br />
                <br />
                {isAXON ? (
                  <>
                    <Button
                      onClick={() => {
                        confirmSendCommand(
                          'CLEANING_START',
                          where +
                            ': Are you sure you want to start the selected charge stations?'
                        );
                      }}
                      color="olive"
                      disabled={!isAXON || !cleaningActive}
                    >
                      Start selected CSs
                    </Button>
                    <br />
                    <br />
                  </>
                ) : (
                  ''
                )}
                <Button
                  onClick={() => {
                    setCleaningSelected([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]); // eslint-disable-line
                    setCleaningActive(false);
                    localStorage.removeItem('hubCleaning');
                  }}
                  color="olive"
                  disabled={!isAXON || !cleaningActive}
                >
                  Stop cleaning
                </Button>
                <br />
                <br />
              </>
            ) : (
              ''
            )}
            {currentUserCan('ManageHydra') && !isAXON && (
              <>
                <Header as="h2">Hydra</Header>
                <Button
                  onClick={() => {
                    confirmSendCommand(
                      'RESTART_HYDRA',
                      'Are you sure you want to restart the Hydra? Please ensure all charging sessions are in B1 state'
                    );
                  }}
                  color="olive"
                >
                  Restart Hydra
                </Button>
                <br />
              </>
            )}
          </Accordion.Content>
        </Accordion>
      </Grid>
      <Grid stretched>
        <Grid.Row>
          <Grid.Column>
            <Accordion>
              <Accordion.Title
                active={!mapHide}
                index={0}
                onClick={() => {
                  setMapHide(!mapHide);
                }}
              >
                <Header as="h2">
                  <Icon name="dropdown" />
                  Map
                </Header>
              </Accordion.Title>
              <Accordion.Content active={!mapHide}>
                {hydra ? (
                  <Map
                    center={mapCenter}
                    zoom={mapZoom}
                    setZoom={setMapZoom}
                    dropPinLongLat={[0, 0]}
                    showHydras={true}
                    showAons={false}
                    showCustomerView={false}
                    setPhalanxTagId={null}
                    showCircle={false}
                    filterHydraRef={hydra?.reference}
                    circleCenter={null}
                    exportParkingData={null}
                    nightMode={false}
                  />
                ) : (
                  ''
                )}
              </Accordion.Content>
            </Accordion>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Modal open={isModalOpen} size="small" style={{ padding: '20px' }}>
        <Modal.Content>
          {confirmation}
          <br />
          <br />
          <br />
          <Button basic color="olive" onClick={sendCommand}>
            Yes
          </Button>
          <Button
            basic
            onClick={event => {
              setIsModalOpen(false);
            }}
          >
            No
          </Button>
        </Modal.Content>
      </Modal>
    </Segment>
  );
};
